/**
 * Human-friendly stack label — Sprint 60.112.
 *
 * The optimizer buckets lineups into 3 structural shapes:
 *   * `primary_only`              → QB + N same-game players (game-stack)
 *   * `primary_secondary`         → QB stack + 2 players in a secondary game
 *   * `primary_secondary_tertiary`→ QB stack + 2 sec + 2 tert
 *
 * Users don't think in those terms — they think "Naked QB / QB+WR /
 * QB+TE+BB" etc. This helper walks the lineup and produces the
 * human-readable label based on the ACTUAL players, with a graceful
 * fallback to the structural name if the players array is missing.
 *
 * Preserves the structural distinction as a suffix when useful
 * (e.g. "QB+WR · 2G" for a QB+WR primary paired with a secondary game).
 */

const SHAPE_SUFFIX = {
  primary_only: "",
  primary_secondary: " · 2G",
  primary_secondary_tertiary: " · 3G",
};

// Sort key so QB+WR+WR always renders WR+WR (not WR+WR order-dependent),
// and QB+WR+TE renders positions in a stable order.
const POS_ORDER = { WR: 1, TE: 2, RB: 3, DST: 4 };

export function humanStackLabel(L) {
  // Showdown: the shape is the team split + who wears the crown.
  if (L?.stack_type === "SHOWDOWN") {
    const cpt = (L.players || []).find((p) => String(p.roster_slot || "").toUpperCase() === "CPT");
    const script = (String(L.stack_desc || "").match(/script\s+(\S+)/) || [])[1];
    const who = cpt ? `CPT ${String(cpt.name || "").split(" ").slice(-1)[0]}` : "";
    return [script, who].filter(Boolean).join(" · ") || "Showdown";
  }
  // No player detail available → structural fallback.
  if (!L?.players?.length) {
    return {
      primary_only: "1-Game Stack",
      primary_secondary: "2-Game Stack",
      primary_secondary_tertiary: "3-Game Stack",
    }[L?.stack_type] || (L?.stack_type || "").replace(/_/g, " ");
  }

  const qb = L.players.find(p => (p.position || "").toUpperCase() === "QB");
  if (!qb) return (L.stack_type || "").replace(/_/g, " ");

  // Same-team teammates (all non-QB positions).
  const sameTeam = L.players.filter(p =>
    p.team === qb.team && p.dk_id !== qb.dk_id
  );
  const passCatchers = sameTeam.filter(p => ["WR", "TE"].includes(p.position));
  const otherOnTeam  = sameTeam.filter(p => !["QB", "WR", "TE"].includes(p.position));

  // Build the core stack label.
  let core;
  if (sameTeam.length === 0) {
    core = "Naked QB";
  } else if (passCatchers.length === 0) {
    // Rare: QB paired with a non-pass-catcher (RB/DST/K on the same team).
    // Explicitly call out the mismatch — user asked for QB+DEF / QB+RB.
    const parts = otherOnTeam
      .map(p => p.position)
      .sort((a, b) => (POS_ORDER[a] || 9) - (POS_ORDER[b] || 9));
    core = `QB+${parts.join("+")}`;
  } else {
    const parts = passCatchers
      .map(p => p.position)
      .sort((a, b) => (POS_ORDER[a] || 9) - (POS_ORDER[b] || 9));
    // If a non-pass-catcher teammate is also present (e.g. QB+WR+RB),
    // append it so the label is truthful.
    if (otherOnTeam.length) {
      parts.push(...otherOnTeam
        .map(p => p.position)
        .sort((a, b) => (POS_ORDER[a] || 9) - (POS_ORDER[b] || 9))
      );
    }
    core = `QB+${parts.join("+")}`;
  }

  return `${core}${SHAPE_SUFFIX[L.stack_type] || ""}`;
}
